import { Grid } from '@material-ui/core';
import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import useInput from 'lib/hooks/useInput';
import MuiButton from 'components/common/button/MuiButton';
import T from 'components/common/text/T';
import CustomSpan from 'components/common/text/CustomSpan';
import UnitPriceModalContainer from 'containers/account/UnitPriceModalContainer';
import AppModal from 'components/common/modal/AppModal';
import PlainModal from 'components/common/modal/PlainModal';
import PencilUnderlineIcon from 'components/base/icons/PencilUnderlineIcon';
import { UnitActions, UtilActions } from 'store/actionCreators';
import IconButton from '@material-ui/core/IconButton';
import { useDidUpdateEffect } from 'lib/utils';
import { useShallowAppSelector } from 'store/hooks';
import { ITeethImplant, ITeethMaterial, ITeethPart } from 'lib/types/sharedTypes';
import { IUnitPriceData } from 'lib/types/accountTypes';
import { addCommas } from 'lib/library';
import { useTranslation } from 'react-i18next';
import { exocadVersion } from 'lib/mapper';
import { userCustomOption } from 'lib/mapper';
import { PageSubTitle } from 'components/base/title/PageTitle';
import ListEmpty from 'components/common/list/ListEmpty';
import CircularLoading from 'components/base/loading/CircularLoading';

interface UnitPirceInfoProps {}

function UnitPriceContainer({}: UnitPirceInfoProps) {
  const {
    user: { userGroupIdx: userId, groupCode, company },
    fetchIndicationInfo: {
      indicationInfo: {
        implantList: indicationImplants,
        materialList: indicationMeterials,
        partList: indicationParts,
      },
    },
    fetchCurrencyList: { currencyList },
    fetchUnitAll: { list: unitPriceList },
    fetchUnitAllSuccess,
    fetchUnitAllPending,
    fetchUnitAllFailure,
    deleteUnitSuccess,
  }: {
    user: { userGroupIdx: string; groupCode: string; company: string };
    fetchIndicationInfo: {
      indicationInfo: {
        implantList: ITeethImplant[];
        materialList: ITeethMaterial[];
        partList: ITeethPart[];
      };
    };
    fetchCurrencyList: { currencyList: any };
    fetchUnitAll: { list: IUnitPriceData[] };
    fetchUnitAllSuccess: boolean;
    fetchUnitAllPending: boolean;
    fetchUnitAllFailure: boolean;
    deleteUnitSuccess: boolean;
  } = useShallowAppSelector(state => ({
    user: state.user.user || {},
    fetchIndicationInfo: state.util.indicationInfo.data || { indicationInfo: {} },
    fetchCurrencyList: state.util.currencyList.data || {},
    fetchUnitAll: state.unit.unitAll.data || {},
    fetchUnitAllSuccess: state.unit.unitAll.success,
    fetchUnitAllPending: state.unit.unitAll.pending,
    fetchUnitAllFailure: state.unit.unitAll.failure,
    deleteUnitSuccess: state.unit.deleteUnit.success,
  }));
  const { t } = useTranslation();
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isModalClose, setIsModalClose] = useState<boolean>(false);

  const modifyUnitPriceKeyCode = useInput('');
  const modifyCurrency = useInput('');
  const isModalModify = useInput(false);

  const handleCheckCloseModal = () => {
    // 팝업 닫힘 감지
    if (!isModalModify.value) {
      handleCloseModal();
    } else {
      setIsModalClose(true);
      setIsModalOpen(true);
    }
    modifyCurrency.setValue('');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsModalClose(false);
    isModalModify.setValue(false);
    setIsLoadFailure(false);
    setIsLoadSuccess(false);
    UnitActions.fetch_unit_all_request({ page: 1, version: exocadVersion });
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setIsModalClose(false);
  };
  const handleRefreshList = () => {
    UnitActions.fetch_unit_all_request({ page: 1, version: exocadVersion });
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  useEffect(() => {
    // init
    UtilActions.fetch_currency_list_request();
    UnitActions.fetch_unit_all_request({ page: 1, version: exocadVersion });
    return () => {
      setIsLoadFailure(false);
      setIsLoadSuccess(false);
    };
  }, []);

  useEffect(() => {
    // init
    UtilActions.fetch_teeth_indication_format_request({
      groupCode: !!groupCode ? groupCode : '',
    });
    UtilActions.fetch_teeth_indication_info_request({
      groupCode: !!groupCode ? groupCode : '',
    });
  }, [groupCode]);

  useDidUpdateEffect(() => {
    if (deleteUnitSuccess) {
      setIsLoadFailure(false);
      setIsLoadSuccess(false);
      UnitActions.fetch_unit_all_request({ page: 1, version: exocadVersion });
    }
  }, [deleteUnitSuccess]);

  useDidUpdateEffect(() => {
    if (!fetchUnitAllSuccess) return;
    setIsLoadSuccess(true);
  }, [fetchUnitAllSuccess]);
  useDidUpdateEffect(() => {
    if (!fetchUnitAllFailure) return;
    setIsLoadFailure(true);
  }, [fetchUnitAllFailure]);

  return (
    <StyledUnitPrice data-component-name="UnitPrice">
      {useMemo(() => {
        return (
          <PlainModal isOpen={isModalOpen} onClose={handleCheckCloseModal} width={1160}>
            <AppModal
              title={t('TITLE_UNIT_PRICE')}
              content={
                <UnitPriceModalContainer
                  modifyUnitPriceKeyCode={modifyUnitPriceKeyCode.value}
                  modifyCurrency={modifyCurrency.value}
                  partnershipIdx=""
                  isModalModify={isModalModify}
                  isModalClose={isModalClose}
                  onCloseModal={handleCloseModal}
                />
              }
              contentCardStyle={{
                padding: '0 0 28px',
                backgroundColor: 'transparent',
              }}
              isCloseIcon={true}
              onClick={handleCheckCloseModal}
              onCancel={handleCheckCloseModal}
              hideButton={true}
            />
          </PlainModal>
        );
      }, [isModalOpen, isModalClose, isModalModify.value, modifyCurrency.value])}
      <div className="unitPrice__container">
        <PageSubTitle title={t('TITLE_UNIT_PRICE')} />
        <div className="unitPrice__content_box">
          <div className="unitPrice__content_button_box">
            <MuiButton
              disableElevation
              className="unit_cost_button"
              variant="contained"
              onClick={() => {
                handleOpenModal();
                modifyUnitPriceKeyCode.onChange({ value: '' });
              }}
            >
              <CustomSpan fontSize={13} fontWeight={400}>
                {'+ '}
                <T>GLOBAL_COST</T>
              </CustomSpan>
            </MuiButton>
          </div>
          <div className="unitPrice__list_table_box ">
            <div className="unitPrice__list_table ">
              <div className="unitPrice__list_table_header">
                <Grid container className="unitPrice__list_table_row">
                  <Grid item className="unit_table_row_item">
                    <T>GLOBAL_NUM</T>
                  </Grid>

                  <Grid item className="unit_table_row_item">
                    <T>GLOBAL_MATERIAL_CONFIG</T>
                  </Grid>
                  <Grid item className="unit_table_row_item">
                    <T>GLOBAL_MATERIALS</T>
                  </Grid>
                  <Grid item className="unit_table_row_item">
                    <T>GLOBAL_OPTION</T>
                  </Grid>
                  <Grid item className="unit_table_row_item">
                    <T>GLOBAL_UNIT_PRICE</T>
                  </Grid>
                  <Grid item className="unit_table_row_item">
                    {' '}
                  </Grid>
                </Grid>
              </div>
              <div className="unitPrice__list_table_body">
                {!isLoadSuccess && fetchUnitAllPending && (
                  <div className="infinite-scroll-loading">
                    <CircularLoading />
                  </div>
                )}
                {!!isLoadSuccess &&
                  (unitPriceList.length > 0 ? (
                    unitPriceList?.map((item: IUnitPriceData, index: number) => {
                      const partItem = indicationParts?.find(
                        (i: ITeethPart) => i.alias === item.unitKey.split('-')[1],
                      );
                      const meterialItem = indicationMeterials?.find(
                        (i: ITeethMaterial) => i.alias === item.unitKey.split('-')[2],
                      );
                      const implantItem = indicationImplants?.find(
                        (i: ITeethImplant) => i.alias === item.unitKey.split('-')[3],
                      );
                      const feature = currencyList?.find(
                        (i: any) => i.code === item.currency,
                      )?.feature;
                      return (
                        <Grid container className="unitPrice__list_table_row " key={index}>
                          <Grid item className="unit_table_row_item">
                            {index + 1}
                          </Grid>
                          <Grid item className="unit_table_row_item">
                            {partItem?.alias.split('_')[0] === userCustomOption.indication
                              ? partItem?.name
                              : t(`INDICATION_${partItem?.alias}`)}
                          </Grid>
                          <Grid item className="unit_table_row_item">
                            {meterialItem?.alias?.split('_')[0] === userCustomOption.material
                              ? meterialItem.name
                              : t(`MATERIAL_${meterialItem?.alias}`)}
                          </Grid>
                          <Grid item className="unit_table_row_item">
                            <CustomSpan fontColor="#b5b7c1">
                              {t(`ABUTMENT_IMPLANT_${implantItem?.alias}`)}
                            </CustomSpan>
                          </Grid>
                          <Grid item className="unit_table_row_item">
                            {feature} {addCommas(item.price)}
                          </Grid>
                          <Grid item className="unit_table_row_item">
                            <IconButton
                              aria-label="cancel modal"
                              className="employeeModal__employee_cancel_btn"
                              onClick={(e: any) => {
                                handleOpenModal();
                                modifyUnitPriceKeyCode.setValue(item.unitKey);
                                modifyCurrency.setValue(item.currency);
                              }}
                            >
                              <PencilUnderlineIcon color="#99D6FB" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      );
                    })
                  ) : (
                    <ListEmpty text="GLOBAL_EMPTY_LIST_DATA" />
                  ))}
                {!!isLoadFailure && (
                  <ListEmpty
                    text="GLOBAL_LIST_ERROR"
                    isRefreshButton={true}
                    onRefresh={handleRefreshList}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </StyledUnitPrice>
  );
}

const StyledUnitPrice = styled.section<{}>`
  .unitPrice__container {
    .unitPrice__title_box {
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      .unitPrice__title_icon {
        width: 60px;
        height: 60px;
      }
    }

    .unitPrice__content_box {
      position: relative;
      height: 448px;

      .unitPrice__content_button_box {
        position: absolute;
        top: -40px;
        right: 0;
        z-index: 1;

        .unit_cost_button {
          width: 126px;
          height: 63px;
          padding-bottom: 23px;
          border-radius: 10px;
          z-index: 1;
          box-shadow: inset 3px 3px 3px rgb(0 0 0 / 16%);
        }
      }

      .unitPrice__list_table_box {
        position: relative;
        z-index: 10;
        width: 100%;
        height: 448px;
        background-color: #ffffff;
        border-radius: 15px;
        box-shadow: 0px 0px 6px rgb(0 0 0 / 16%);
        padding: 13px 25px 0;
        .unitPrice__list_table {
          height: 100%;
          .unitPrice__list_table_header {
            width: 100%;
            height: 40px;
            border-radius: 5px;
            background-color: #edf4fb;
            .unitPrice__list_table_row {
              height: 40px;
            }
          }
          .unitPrice__list_table_body {
            margin-right: -25px;
            overflow-y: overlay;
            font-size: 16px;
            height: 385px;
            position: relative;
            width: 100%;

            .unitPrice__list_table_row {
              height: 61px;
              border-bottom: 1px solid #edf4fb;
            }
          }
          .unitPrice__list_table_row {
            width: 100%;
            align-items: center;
            .unit_table_row_item {
              font-size: 15px;
              font-weight: 400;
              &:nth-child(1) {
                width: 80px;
                font-weight: 200;
                color: #b5b7c1;
                text-align: center;
              }
              &:nth-child(2) {
                width: 250px;
                font-size: 16px;
              }
              &:nth-child(3) {
                width: 260px;
                font-size: 16px;
              }
              &:nth-child(4) {
                width: 330px;
              }
              &:nth-child(5) {
                width: 160px;
                padding-right: 30px;
                text-align: right;
              }
              &:nth-child(6) {
                width: 60px;
                text-align: center;
              }
            }
          }

          .delete_unit_icon {
            img {
              width: 15px;
              height: 17px;
            }
          }
        }
      }
    }
  }
`;

export default React.memo(UnitPriceContainer);
